"use client"

import { motion } from "framer-motion"
import { Brain, Zap, Activity, Sparkles, ArrowRight, Eye, Network } from "lucide-react"
import { useState } from "react"

export default function LandingPage({ onGetStarted }) {
  const [particles] = useState(
    Array.from({ length: 50 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 4 + 2,
      duration: Math.random() * 20 + 10,
    }))
  )

  return (
    <div className="landing-page">
      {/* Animated Background Particles */}
      <div className="particles-container">
        {particles.map((particle) => (
          <motion.div
            key={particle.id}
            className="particle"
            style={{
              left: `${particle.x}%`,
              top: `${particle.y}%`,
              width: particle.size,
              height: particle.size,
            }}
            animate={{
              y: [0, -30, 0],
              opacity: [0.2, 0.8, 0.2],
            }}
            transition={{
              duration: particle.duration,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        ))}
      </div>

      {/* Neural Network Background */}
      <div className="neural-bg">
        <svg className="neural-svg" viewBox="0 0 1000 1000">
          <defs>
            <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#06ffa5" stopOpacity="0.3" />
              <stop offset="100%" stopColor="#667eea" stopOpacity="0.3" />
            </linearGradient>
          </defs>
          {Array.from({ length: 20 }).map((_, i) => (
            <motion.line
              key={i}
              x1={Math.random() * 1000}
              y1={Math.random() * 1000}
              x2={Math.random() * 1000}
              y2={Math.random() * 1000}
              stroke="url(#lineGradient)"
              strokeWidth="1"
              initial={{ pathLength: 0, opacity: 0 }}
              animate={{ pathLength: 1, opacity: 0.5 }}
              transition={{
                duration: 2,
                delay: i * 0.1,
                repeat: Infinity,
                repeatType: "reverse",
              }}
            />
          ))}
        </svg>
      </div>

      {/* Main Content */}
      <div className="landing-content">
        {/* Hero Section */}
        <motion.div
          className="hero-section"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          {/* Logo Animation */}
          <motion.div
            className="landing-logo"
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: "spring", stiffness: 100, delay: 0.2 }}
          >
            <div className="logo-glow">
              <Brain size={80} className="brain-icon" />
            </div>
          </motion.div>

          {/* Title */}
          <motion.h1
            className="landing-title"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <span className="gradient-text">Neuro</span>
            <span className="white-text">Ease</span>
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            className="landing-subtitle"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
          >
            Advanced Brain Activity Analysis & Visualization Platform
          </motion.p>

          {/* Tagline */}
          <motion.div
            className="landing-tagline"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
          >
            <Sparkles size={20} className="sparkle-icon" />
            <span>Powered by AI • Real-time Analysis • 3D Visualization</span>
            <Sparkles size={20} className="sparkle-icon" />
          </motion.div>

          {/* CTA Button */}
          <motion.button
            className="cta-button"
            onClick={onGetStarted}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 1, type: "spring", stiffness: 200 }}
            whileHover={{ scale: 1.05, boxShadow: "0 0 40px rgba(6, 255, 165, 0.6)" }}
            whileTap={{ scale: 0.95 }}
          >
            <span>Get Started</span>
            <ArrowRight size={24} />
          </motion.button>
        </motion.div>

        {/* Features Grid */}
        <motion.div
          className="features-grid"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2 }}
        >
          {[
            {
              icon: Brain,
              title: "3D Brain Visualization",
              description: "Interactive 3D models with real-time region highlighting",
              color: "#667eea",
            },
            {
              icon: Network,
              title: "Neural Networks",
              description: "Visualize neural pathways and connections",
              color: "#06ffa5",
            },
            {
              icon: Activity,
              title: "Real-time Analysis",
              description: "AI-powered cognitive activity insights",
              color: "#ff006e",
            },
            {
              icon: Zap,
              title: "Advanced Analytics",
              description: "Comprehensive brain performance metrics",
              color: "#ffd60a",
            },
          ].map((feature, index) => (
            <motion.div
              key={index}
              className="feature-card"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.4 + index * 0.1 }}
              whileHover={{ y: -10, scale: 1.02 }}
            >
              <div className="feature-icon" style={{ backgroundColor: `${feature.color}20` }}>
                <feature.icon size={32} style={{ color: feature.color }} />
              </div>
              <h3>{feature.title}</h3>
              <p>{feature.description}</p>
            </motion.div>
          ))}
        </motion.div>

        {/* Floating Elements */}
        <motion.div
          className="floating-element floating-1"
          animate={{
            y: [0, -20, 0],
            rotate: [0, 10, 0],
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        >
          <Eye size={40} />
        </motion.div>

        <motion.div
          className="floating-element floating-2"
          animate={{
            y: [0, 20, 0],
            rotate: [0, -10, 0],
          }}
          transition={{
            duration: 5,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        >
          <Zap size={40} />
        </motion.div>
      </div>
    </div>
  )
}
